<template>
  <div class="clearfix">
    <!-- 文件上传组件 -->
    <a-upload
      :disabled="disabled"
      :file-list="fileList"
      :list-type="type === 'image' ? 'picture-card' : 'picture'"
      :show-upload-list="true"
      :headers="headers"
      :before-upload="beforeUpload"
      :customRequest="handleChange"
      @change="changeImg"
      @preview="handlePreview"
    >
      <!-- 文件、视频类型：显示上传按钮 -->
      <div v-if="type === 'file' || type === 'video'">
        <a-button> <a-icon type="upload" /> 上传 </a-button>
      </div>
      <!-- 图片类型：显示上传图标（当未达到最大数量时，禁用状态下也会显示占位） -->
      <div v-if="fileList.length < count">
        <span v-if="type === 'image'">
          <a-icon :type="loading ? 'loading' : 'plus'" />
          <div class="ant-upload-text">上传</div>
        </span>
      </div>
    </a-upload>

    <!-- 图片预览弹窗 -->
    <a-modal 
      :visible="previewVisible" 
      :footer="null" 
      @cancel="handleCancel"
      :width="800"
      centered
      :maskClosable="true"
      :bodyStyle="{ padding: '20px', height: '800px', display: 'flex', alignItems: 'center', justifyContent: 'center' }"
    >
      <div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center">
        <img
          alt="预览图片"
          style="max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; cursor: zoom-in; display: block"
          :src="previewImage"
        />
      </div>
    </a-modal>
  </div>
</template>

<script>
import { $on, $off, $once, $emit } from '../../utils/gogocodeTransfer'
/**
 * 文件上传组件
 *
 * 功能说明：
 * 1. 支持单图和多图上传（通过 count 属性控制）
 * 2. 支持图片、文件、视频类型上传（通过 type 属性控制）
 * 3. 使用 OSS 上传，上传成功后返回文件 URL
 * 4. 支持图片预览功能
 * 5. 支持文件删除功能
 *
 * 使用示例：
 * <file-upload
 *   :value="form.imageUrl"
 *   type="image"
 *   :count="1"
 *   @change="handleImageChange"
 * />
 *
 * @author zbtech
 * @date 2025-11-08
 */
import storage from 'store'
import { ACCESS_TOKEN } from '@/store/mutation-types'
import UploadFileToOSS from '@/utils/upload-file-to-oss.js'

/**
 * 获取文件的 base64 编码
 * 用于图片预览功能
 *
 * @param {File} file - 文件对象
 * @returns {Promise<String>} base64 编码的字符串
 */
function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
  })
}

export default {
  name: 'ImageUpload',
  props: {
    /** 是否禁用上传 */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 文件URL值（v-model）
     * 单图模式：直接是URL字符串，如 "https://example.com/image.jpg"
     * 多图模式：逗号分隔的URL字符串，如 "url1,url2,url3"
     */
    value: {
      type: String,
      default: '',
    },
    /**
     * 上传的文件类型
     * - 'image': 图片类型（默认）
     * - 'file': 文件类型
     * - 'video': 视频类型
     */
    type: {
      type: String,
      default: 'image',
    },
    /**
     * 字段名称（用于多图模式）
     * 多图模式下，会生成 fieldName 和 fieldNameArr 两个字段
     */
    fieldName: {
      type: String,
      default: 'path',
    },
    /**
     * 最大上传数量
     * 1: 单图模式，返回单个URL字符串
     * >1: 多图模式，返回逗号分隔的URL字符串或对象
     */
    count: {
      type: Number,
      default: 1,
    },
  },
  watch: {
    /**
     * 监听 value 变化，同步更新 fileList
     * 当父组件传入的 value 发生变化时，自动更新内部 fileList
     */
    value: {
      deep: true,

      handler(newVal) {
        this.updateFileListFromValue(newVal)
      },

      // 组件创建时立即执行一次
      immediate: true,
    },
  },
  data() {
    return {
      /** 上传加载状态 */
      loading: false,
      /** HTTP 请求头（包含认证token） */
      headers: {
        Authorization: 'Bearer ' + storage.get(ACCESS_TOKEN),
      },
      /** 预览弹窗显示状态 */
      previewVisible: false,
      /** 预览图片URL */
      previewImage: '',
      /** 文件列表（Ant Design Upload 组件要求的格式） */
      fileList: [],
    }
  },
  methods: {
    /**
     * 根据 value 更新 fileList
     *
     * 将父组件传入的 URL 字符串转换为 Ant Design Upload 组件需要的文件列表格式
     * 每个文件对象必须包含：uid（唯一标识）、name（文件名）、url（文件URL）、status（状态）
     *
     * @param {String} value - 图片URL字符串
     *   - 单图模式：直接是URL，如 "https://example.com/image.jpg"
     *   - 多图模式：逗号分隔的URL，如 "url1,url2,url3"
     */
    updateFileListFromValue(value) {
      // 如果值为空，清空文件列表
      if (!value || value === '') {
        this.fileList = []
        return
      }

      /**
       * 从URL中提取文件名
       * 支持完整URL（http://...）和相对路径
       *
       * @param {String} url - 文件URL
       * @returns {String} 文件名
       */
      const getFileName = (url) => {
        if (!url) return 'image.jpg'
        try {
          // 尝试解析完整URL
          const urlObj = new URL(url)
          const pathname = urlObj.pathname
          const fileName = pathname.split('/').pop() || 'image.jpg'
          return fileName
        } catch (e) {
          // 如果不是完整URL，从路径中提取文件名
          const parts = url.split('/')
          return parts[parts.length - 1] || 'image.jpg'
        }
      }

      if (this.count === 1) {
        // 单图模式：创建一个文件对象
        const url = value.trim()
        this.fileList = [
          {
            uid: `file-${Date.now()}-${Math.random()
              .toString(36)
              .substr(2, 9)}`, // 生成唯一ID
            name: getFileName(url), // 从URL提取文件名
            url: url, // 文件URL
            path: url, // 文件路径（兼容字段）
            status: 'done', // 状态：已完成
          },
        ]
      } else {
        // 多图模式：将逗号分隔的URL字符串转换为文件对象数组
        const urls = value.split(',').filter((url) => url && url.trim())
        this.fileList = urls.map((url, idx) => {
          const trimmedUrl = url.trim()
          return {
            uid: `file-${Date.now()}-${idx}-${Math.random()
              .toString(36)
              .substr(2, 9)}`, // 生成唯一ID
            name: getFileName(trimmedUrl), // 从URL提取文件名
            url: trimmedUrl, // 文件URL
            path: trimmedUrl, // 文件路径（兼容字段）
            status: 'done', // 状态：已完成
          }
        })
      }
    },
    /**
     * 处理图片预览
     * 当用户点击图片时，显示预览弹窗
     *
     * @param {Object} file - 文件对象
     */
    async handlePreview(file) {
      // 如果文件没有URL和预览图，则生成base64预览图
      if (!file.url && !file.preview) {
        file.preview = await getBase64(file.originFileObj)
      }
      // 设置预览图片URL并显示弹窗
      this.previewImage = file.url || file.preview
      this.previewVisible = true
    },
    /**
     * 上传前的文件校验
     * 检查文件大小是否超过限制（2MB）
     *
     * @param {File} file - 文件对象
     * @returns {Boolean} true-允许上传，false-不允许上传
     */
    beforeUpload(file) {
      // 文件大小限制：2MB
      const isLt2M = file.size / 1024 / 1024 < 2
      if (!isLt2M) {
        this.$message.error('图片大小限制 2MB!')
      }
      return isLt2M
    },
    /**
     * 处理文件上传
     * 使用 OSS 上传服务上传文件
     *
     * @param {Object} info - 上传信息对象
     * @param {File} info.file - 要上传的文件对象
     */
    async handleChange(info) {
      this.loading = true
      // 调用 OSS 上传服务
      new UploadFileToOSS([info.file], {
        finally: (res) => {
          this.loading = false
          if (res && res.length > 0) {
            // 格式化返回的文件对象，确保符合 Ant Design Upload 组件的要求
            const formattedRes = res.map((item) => ({
              uid:
                item.uid ||
                `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // 唯一标识
              name: item.name || 'image.jpg', // 文件名
              status: item.status || 'done', // 状态
              url: item.url || item.path, // 文件URL
              path: item.path || item.url, // 文件路径（兼容字段）
              ...item, // 保留其他字段（如 halfUrl、cover 等）
            }))
            // 将新上传的文件添加到文件列表
            this.fileList = this.fileList.concat(formattedRes)

            if (this.count === 1) {
              // 单图模式：直接返回URL字符串给父组件
              $emit(this, 'change', res[0].path || res[0].url)
            } else {
              // 多图模式：调用 getImg 处理并返回对象格式
              this.getImg(this.fileList, this.fieldName)
            }
          }
        },
      })
    },
    /**
     * 处理多图模式的数据格式转换
     *
     * 将文件列表数组转换为父组件需要的格式：
     * - fieldName: 逗号分隔的URL字符串（如 "url1,url2,url3"）
     * - fieldNameArr: 文件对象数组
     *
     * @param {Array} info - 文件列表数组
     * @param {String} fieldName - 字段名称（如 "certificateUrl"）
     */
    getImg(info, fieldName) {
      const toChangeForm = {}

      if (info.length === 0) {
        // 文件列表为空时，清空字段
        toChangeForm[fieldName] = ''
        toChangeForm[`${fieldName}Arr`] = []
      } else {
        if (this.count === 1) {
          // 单图模式（虽然不应该走到这里，但为了安全还是处理）
          toChangeForm[fieldName] = info[0].url || info[0].path
          toChangeForm[`${fieldName}Arr`] = info
        } else {
          // 多图模式：将URL数组拼接成逗号分隔的字符串
          toChangeForm[fieldName] = info
            .map((ele) => ele.url || ele.path) // 提取URL
            .filter((url) => url) // 过滤空值
            .join(',') // 用逗号连接
          toChangeForm[`${fieldName}Arr`] = info // 保留完整的文件对象数组
        }
      }

      // 如果 value 是对象，则合并；否则直接发送 toChangeForm
      if (typeof this.value === 'object' && this.value !== null) {
        $emit(this, 'change', { ...this.value, ...toChangeForm })
      } else {
        $emit(this, 'change', toChangeForm)
      }
    },
    /**
     * 处理文件列表变化（主要用于删除文件）
     *
     * 当用户点击删除按钮时，从文件列表中移除对应文件并通知父组件
     *
     * @param {Object} info - 文件变化信息
     * @param {Object} info.file - 变化的文件对象
     * @param {String} info.file.status - 文件状态（'removed' 表示已删除）
     * @param {String} info.file.uid - 文件唯一标识
     */
    changeImg(info) {
      // 只处理删除操作
      if (info.file.status === 'removed') {
        if (this.count === 1) {
          // 单图模式：清空文件列表并通知父组件
          this.fileList = []
          $emit(this, 'change', '')
        } else {
          // 多图模式：从列表中移除指定文件，并更新父组件数据
          const index = this.fileList.findIndex(
            (ele) => ele.uid === info.file.uid
          )
          if (index !== -1) {
            this.fileList.splice(index, 1)
            // 更新父组件数据（重新计算URL字符串）
            this.getImg(this.fileList, this.fieldName)
          }
        }
      }
    },
    /**
     * 关闭预览弹窗
     */
    handleCancel() {
      this.previewVisible = false
    },
  },
  emits: ['change', 'update:value', , , 'update:value'],
}
</script>

<style lang="less" scoped>
img {
  width: 128px;
  height: 128px;
}
.ant-upload-select-picture-card i {
  font-size: 32px;
  color: #999;
}
.ant-upload-select-picture-card .ant-upload-text {
  margin-top: 8px;
  color: #666;
}
</style>
